import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, brl, formatDateTime } from "../../lib/api";
import { toast } from "sonner";
import { Camera, MapPin, CheckCircle, Shield } from "@phosphor-icons/react";

export default function SignPromissory() {
  const { saleId } = useParams();
  const [info, setInfo] = useState(null);
  const [selfie, setSelfie] = useState("");
  const [geo, setGeo] = useState(null);
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const fileRef = useRef();
  const videoRef = useRef();
  const canvasRef = useRef();
  const [stream, setStream] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/sales/${saleId}/sign-info`);
        setInfo(data);
        if (data.already_signed) setDone(data.promissory);
      } catch (e) {
        toast.error("Venda nao encontrada");
      }
    })();
  }, [saleId]);

  const startCamera = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Camera nao suportada nesse navegador - use upload");
      fileRef.current?.click();
      return;
    }
    if (!window.isSecureContext) {
      toast.error("Camera requer HTTPS - use upload");
      fileRef.current?.click();
      return;
    }
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "user" }, width: { ideal: 640 }, height: { ideal: 640 } },
        audio: false,
      });
      setStream(s);
      // wait one tick for video element to be ready
      setTimeout(async () => {
        if (videoRef.current) {
          videoRef.current.srcObject = s;
          try { await videoRef.current.play(); } catch (e) { /* autoplay can fail silently */ }
        }
      }, 50);
    } catch (err) {
      console.error("getUserMedia error:", err);
      const msg = err?.name === "NotAllowedError"
        ? "Permissao da camera negada - habilite nas configuracoes ou use upload"
        : err?.name === "NotFoundError"
        ? "Nenhuma camera encontrada - use upload"
        : "Camera indisponivel - use upload";
      toast.error(msg);
      fileRef.current?.click();
    }
  };
  const stopCamera = () => {
    stream?.getTracks().forEach((t) => t.stop());
    setStream(null);
  };
  const snap = () => {
    const v = videoRef.current; const c = canvasRef.current;
    if (!v || !c) return;
    c.width = v.videoWidth; c.height = v.videoHeight;
    c.getContext("2d").drawImage(v, 0, 0);
    setSelfie(c.toDataURL("image/jpeg", 0.85));
    stopCamera();
  };
  const onFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const r = new FileReader(); r.onload = () => setSelfie(r.result); r.readAsDataURL(f);
  };
  const captureGeo = () => {
    if (!navigator.geolocation) return toast.error("Seu navegador nao suporta GPS");
    if (!window.isSecureContext) {
      return toast.error("Localizacao exige HTTPS. Acesse o site pelo dominio com cadeado (SSL/certbot).");
    }
    toast.loading("Obtendo localizacao...", { id: "geo" });
    navigator.geolocation.getCurrentPosition(
      (p) => {
        setGeo({ lat: p.coords.latitude, lng: p.coords.longitude, label: `lat ${p.coords.latitude.toFixed(4)}, lng ${p.coords.longitude.toFixed(4)}` });
        toast.success("Localizacao OK", { id: "geo" });
      },
      (err) => {
        const msg = err.code === 1
          ? "Permissao de localizacao negada - habilite o GPS nas configuracoes do navegador"
          : err.code === 2
          ? "Localizacao indisponivel - verifique o GPS do dispositivo"
          : err.code === 3
          ? "Tempo esgotado ao obter localizacao - tente novamente"
          : "Nao foi possivel obter a localizacao";
        toast.error(msg, { id: "geo" });
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
  };

  const submit = async () => {
    if (!selfie) return toast.error("Capture sua selfie");
    if (!geo) return toast.error("Compartilhe sua localizacao");
    if (!accepted) return toast.error("Aceite os termos");
    setSubmitting(true);
    try {
      const { data } = await api.post(`/public/sales/${saleId}/sign`, {
        selfie_data_url: selfie,
        geolocation: geo,
        device: navigator.userAgent.slice(0, 120),
        accepted: true,
      });
      setDone(data);
      toast.success("Promissoria assinada com sucesso");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao assinar");
    } finally { setSubmitting(false); }
  };

  if (!info) return <div className="min-h-screen grid place-items-center text-sm tracking-widest uppercase text-[#5C5A57]">Carregando...</div>;

  if (done) {
    const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}/api/promissories/${done.id}/pdf`;
    return (
      <div className="min-h-screen bg-[#F5F0EB] grid place-items-center p-6">
        <div className="bg-white border border-[#EBE5DF] max-w-md w-full p-10 text-center">
          <CheckCircle size={48} weight="duotone" color="#7B9D7E" className="mx-auto mb-4" />
          <h1 className="font-display text-3xl font-bold tracking-tight">Promissoria assinada</h1>
          <p className="text-sm text-[#5C5A57] mt-3">N. {done.number} - {formatDateTime(done.issued_at)}</p>
          <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="brand inline-block mt-6" data-testid="download-pdf-public">Baixar PDF</a>
          <div className="mt-8 text-[10px] text-[#5C5A57] tracking-widest uppercase">Cris Lingerie · Banco das Revendedoras</div>
        </div>
      </div>
    );
  }

  const { sale, client } = info;

  return (
    <div className="min-h-screen" style={{ background: "#F5F0EB" }} data-testid="sign-promissory-page">
      <div className="max-w-3xl mx-auto p-6 md:p-12">
        <div className="text-center mb-8">
          <div className="font-display text-3xl font-bold tracking-tight">Cris Lingerie</div>
          <div className="text-[11px] tracking-[0.22em] uppercase mt-1 text-[#5C5A57]">Banco das Revendedoras · Promissoria Digital</div>
        </div>

        <div className="bg-white border border-[#EBE5DF] p-8 mb-6 relative overflow-hidden">
          <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{ background: "radial-gradient(circle at 30% 20%, #DDA7A5 0%, transparent 60%)" }} />
          <div className="relative">
            <div className="text-[10px] tracking-[0.2em] uppercase text-[#5C5A57]">Venda #{sale.number}</div>
            <h1 className="font-display text-3xl font-bold tracking-tight mt-1 mb-6">Confirme a compra</h1>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <div className="text-[10px] tracking-widest uppercase text-[#5C5A57]">Cliente</div>
                <div className="font-medium mt-1">{client.name}</div>
                <div className="text-xs text-[#5C5A57]">{client.phone}</div>
              </div>
              <div>
                <div className="text-[10px] tracking-widest uppercase text-[#5C5A57]">Total</div>
                <div className="font-display text-2xl font-bold tabular">{brl(sale.total)}</div>
                <div className="text-xs text-[#5C5A57]">{sale.installments}x · {sale.payment_method}</div>
              </div>
            </div>

            <div className="border-t border-[#EBE5DF] pt-4">
              <div className="text-[10px] tracking-widest uppercase text-[#5C5A57] mb-2">Produtos</div>
              <ul className="text-sm space-y-1">
                {sale.products.map((p, i) => (
                  <li key={i} className="flex justify-between border-b border-[#EBE5DF] py-2">
                    <span>{p.qty}x {p.name}</span>
                    <span className="tabular">{brl(p.qty * p.price)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="bg-white border border-[#EBE5DF] p-8 mb-6">
          <h2 className="section-title mb-4">Identificacao</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="aspect-square border border-[#EBE5DF] grid place-items-center bg-[#F5F0EB] overflow-hidden">
                {selfie ? <img src={selfie} alt="selfie" className="w-full h-full object-cover" /> :
                  stream ? <video ref={videoRef} className="w-full h-full object-cover" playsInline muted /> :
                  <div className="text-center text-[#5C5A57]"><Camera size={32} weight="thin" /><div className="text-xs mt-2">Selfie obrigatoria</div></div>}
              </div>
              <canvas ref={canvasRef} hidden />
              <div className="flex gap-2 mt-3">
                {!stream && !selfie && <button onClick={startCamera} className="brand flex-1" data-testid="start-camera-btn">Tirar selfie</button>}
                {stream && <button onClick={snap} className="brand flex-1" data-testid="snap-btn">Capturar</button>}
                {selfie && <button onClick={() => setSelfie("")} className="text-xs tracking-widest uppercase font-medium hover:underline">Refazer</button>}
                <input ref={fileRef} type="file" accept="image/*" onChange={onFile} hidden />
                {!selfie && !stream && <button onClick={() => fileRef.current?.click()} className="text-xs tracking-widest uppercase font-medium hover:underline">ou Upload</button>}
              </div>
            </div>

            <div>
              <div className="aspect-square border border-[#EBE5DF] bg-[#F5F0EB] grid place-items-center p-4 text-center">
                <div>
                  <MapPin size={32} weight="thin" />
                  <div className="text-xs text-[#5C5A57] mt-2">{geo ? geo.label : "Compartilhe sua localizacao"}</div>
                </div>
              </div>
              <button onClick={captureGeo} className="brand w-full mt-3" data-testid="capture-geo-public-btn">{geo ? "Atualizar localizacao" : "Compartilhar localizacao"}</button>
            </div>
          </div>
        </div>

        <div className="bg-white border border-[#EBE5DF] p-8 mb-6">
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} className="mt-1" data-testid="accept-terms-checkbox" />
            <span className="text-sm text-[#5C5A57] leading-relaxed">
              Declaro que sou {client.name} ({client.cpf?.slice(0, 3)}.***), reconheco a compra acima
              e me comprometo ao pagamento integral. Autorizo o registro de selfie, localizacao e
              dispositivo como prova de aceite eletronico. Documento eletronico com validade juridica.
            </span>
          </label>
        </div>

        <button onClick={submit} className="brand w-full" disabled={submitting} data-testid="submit-sign-btn">
          <Shield size={16} className="inline mr-2" />
          {submitting ? "Assinando..." : "Assinar Promissoria Digital"}
        </button>

        <div className="text-center text-[10px] tracking-widest uppercase text-[#5C5A57] mt-6">
          Cris Lingerie © Banco das Revendedoras
        </div>
      </div>
    </div>
  );
}
